import { FunctionComponent, useState } from 'react'
import { styles } from '../../../../styles/themes/style'
import styled from 'styled-components'
import Column from '../../../../components/Column'
import Row from '../../../../components/Row'
import Text from '../../../../components/Text'
import { useOwnApiKey } from './useOwnApiKey'
import { ApiKeyCodeBlock, MASKED_API_KEY } from './ApiKeyVisibility'

const MCP_HELP_ARTICLE_URL = 'https://help.leaddelta.com/en/article/leaddelta-linkedin-mcp-server-search-your-network-build-lead-lists-and-run-linkedin-campaigns-from-claude-chatgpt-or-cursor-ntwpy4/'

interface IMcpServerDocsProps {}

type SetupTab = 'streamable' | 'sse'
type ToolTab = 'list' | 'filter' | 'leads' | 'intros'

/**
 * read  — looks something up, changes nothing
 * write — creates or edits something, reversibly
 * care  — deletes, spends credits, or contacts real people
 */
type ToolType = 'read' | 'write' | 'care'

const TOOL_GROUPS: { title: string, tools: [string, string, ToolType][] }[] = [
    {
        title: 'Your network',
        tools: [
            ['get_connections_mcp', 'Search connections by name, company, job title, location, headline or tag — everyone with a tag, several tags, or a tag but not another — and count them', 'read'],
            ['get_connections_list', 'List all connections, page by page', 'read'],
            ['get_connection', "One person's full record — profile, tags, notes and contact details. Accepts a LeadDelta id or a LinkedIn profile link", 'read'],
            ['get_connection_history', 'Work and education history for up to 50 people at once', 'read'],
        ]
    },
    {
        title: 'Finding new people',
        tools: [
            ['search_leads', 'Search the lead database for people not in your network — job title, company, industry, size, location, school and more', 'read'],
        ]
    },
    {
        title: 'Warm introductions',
        tools: [
            ['get_connection_recommendations', 'Read who can introduce you to someone, why, and a drafted ask', 'read'],
            ['generate_connection_recommendations', 'Work out fresh introduction paths — costs 10 credits', 'care'],
        ]
    },
    {
        title: 'Tags, notes and follow-ups',
        tools: [
            ['list_tags', 'Workspace tags with how many people carry each, and which are public or private', 'read'],
            ['create_tag', 'Create a tag, public or private, or reuse one that already exists by that name', 'write'],
            ['update_tag', 'Rename a tag, change its colour, or make it public or private', 'write'],
            ['update_connection_tags', 'Add or remove tags on a person', 'write'],
            ['create_note', "Write a note on someone's record, private or public, and @mention teammates", 'write'],
            ['update_note', 'Edit a note, make it public or private, or delete it', 'care'],
            ['list_workspace_members', 'The members of your workspace, to assign tasks to or mention in notes', 'read'],
            ['list_tasks', 'Tasks and reminders — by person, by who they are assigned to, or by who created them', 'read'],
            ['create_task', 'Create a task or reminder, optionally linked to people and assigned to a teammate', 'write'],
            ['update_task', 'Complete, edit, reassign or delete a task or reminder', 'care'],
            ['list_job_changes', 'People in your network who recently changed role or employer', 'read'],
            ['list_templates', 'Your saved message templates, with their text', 'read'],
        ]
    },
    {
        title: 'Contact details',
        tools: [
            ['quote_enrichment', 'Price a contact lookup in credits — charges nothing', 'write'],
            ['run_enrichment', 'Run the lookup and spend the credits', 'care'],
        ]
    },
    {
        title: 'Campaigns — seeing what is happening',
        tools: [
            ['list_campaigns', 'Your campaigns and their status', 'read'],
            ['get_campaign', 'One campaign in full, including whether it is ready to send', 'read'],
            ['get_campaign_leads', 'The people enrolled in a campaign', 'read'],
            ['get_lead_activity', 'What happened with one person — sent, opened, replied', 'read'],
            ['get_campaign_stats', 'How a campaign is performing overall', 'read'],
            ['list_linkedin_accounts', 'The LinkedIn accounts you can send from', 'read'],
            ['get_linkedin_account_limits', "An account's daily sending limit and how much is left", 'read'],
        ]
    },
    {
        title: 'Campaigns — setting one up',
        tools: [
            ['create_campaign', 'Create a campaign as a draft', 'write'],
            ['configure_campaign', 'Set targeting, sending window and the sequence — profile visits, invites, messages, InMail / Open Profile messages, post likes and comments, email', 'write'],
            ['update_campaign_accounts', 'Choose which LinkedIn accounts it sends from', 'write'],
        ]
    },
    {
        title: 'Campaigns — choosing who to contact',
        tools: [
            ['add_leads_from_connections', 'Enrol people already in your network', 'write'],
            ['search_prospects', 'Find candidates for this campaign, skipping anyone already in it', 'read'],
            ['add_leads_from_prospects', 'Enrol candidates from that search', 'write'],
            ['add_leads_from_rows', 'Enrol people from a list you provide', 'write'],
            ['remove_campaign_leads', 'Remove people from a campaign', 'care'],
        ]
    },
    {
        title: 'Campaigns — sending',
        tools: [
            ['update_campaign_status', 'Start, pause or cancel. Starting begins contacting people', 'care'],
        ]
    },
]

const TYPE_LABEL: Record<ToolType, string> = { read: 'Read', write: 'Write', care: 'Careful' }

const McpServerDocs: FunctionComponent<IMcpServerDocsProps> = () => {
    const ownApiKey = useOwnApiKey()
    const [keyVisible, setKeyVisible] = useState(false)
    const [setupTab, setSetupTab] = useState<SetupTab>('streamable')
    const [toolTab, setToolTab] = useState<ToolTab>('filter')

    // Masked in both configurations until shown with the eye on either block.
    const apiKey = ownApiKey ? (keyVisible ? ownApiKey : MASKED_API_KEY) : 'YOUR_API_KEY'
    const toggleKeyVisible = () => setKeyVisible(previous => !previous)

    const streamableConfig = `{
  "mcpServers": {
    "leaddelta": {
      "type": "streamable-http",
      "url": "https://mcp.leaddelta.com/mcp",
      "headers": {
        "Authorization": "Bearer ${apiKey}"
      }
    }
  }
}`

    const sseConfig = `{
  "mcpServers": {
    "leaddelta": {
      "type": "sse",
      "url": "https://mcp.leaddelta.com/sse",
      "headers": {
        "key": "${apiKey}"
      }
    }
  }
}`

    return (
        <Container>
            <Column gap='32px'>

                {/* Header */}
                <Column gap='6px'>
                    <HeaderRow gap='10px' alignItems>
                        <Text $heading4>LeadDelta MCP Server</Text>
                        <BetaBadge>BETA</BetaBadge>
                    </HeaderRow>
                    <Paragraph>
                        Connect LeadDelta to AI assistants using the Model Context Protocol (MCP).
                        This feature is in early access — the server URL, tool names, and parameters may change as we iterate.
                        Learn more about the LeadDelta MCP in this <DocLink href={MCP_HELP_ARTICLE_URL} target="_blank" rel="noopener noreferrer">help article</DocLink>.
                    </Paragraph>
                </Column>

                {/* What it costs */}
                <Column gap='12px'>
                    <Text $heading5>Before anything costs you</Text>
                    <Paragraph>
                        Three actions spend something real. Your assistant is told to confirm with you first, but it is worth knowing which they are.
                    </Paragraph>
                    <CostTable>
                        <thead>
                            <tr><th>Action</th><th>What it costs</th></tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Looking up contact details</td>
                                <td>Enrichment credits — 1 per work email, 3 per personal email, 10 per phone number, with a discount on larger batches</td>
                            </tr>
                            <tr>
                                <td>Finding warm intro paths</td>
                                <td>10 enrichment credits per person you want to reach</td>
                            </tr>
                            <tr>
                                <td>Starting a campaign</td>
                                <td>No credits, but it begins sending real messages from your LinkedIn account</td>
                            </tr>
                        </tbody>
                    </CostTable>
                </Column>

                {/* What you can do */}
                <Column gap='12px'>
                    <Text $heading5>Available tools</Text>
                    <Paragraph>
                        Once connected, your AI assistant can use these on your behalf. You speak to it normally —
                        it picks the right tool itself.
                    </Paragraph>
                    <LegendRow gap='14px' alignItems>
                        <LegendItem><TypeBadge $type='read'>Read</TypeBadge> looks something up, changes nothing</LegendItem>
                        <LegendItem><TypeBadge $type='write'>Write</TypeBadge> creates or edits, reversibly</LegendItem>
                        <LegendItem><TypeBadge $type='care'>Careful</TypeBadge> deletes, spends credits, or contacts people</LegendItem>
                    </LegendRow>

                    {TOOL_GROUPS.map(({ title, tools }) => (
                        <Column key={title} gap='6px'>
                            <SectionLabel>{title}</SectionLabel>
                            <ToolsTable>
                                <tbody>
                                    {tools.map(([name, description, type]) => (
                                        <tr key={name}>
                                            <td><InlineCode>{name}</InlineCode></td>
                                            <td>{description}</td>
                                            <td><TypeBadge $type={type}>{TYPE_LABEL[type]}</TypeBadge></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </ToolsTable>
                        </Column>
                    ))}
                </Column>

                {/* Setup */}
                <Column gap='0'>
                    <Text $heading5>Setup</Text>
                    <SetupDesc>
                        Add LeadDelta to your MCP client configuration. Use the <strong>Streamable HTTP</strong> transport (recommended) or <strong>SSE</strong> as a fallback if your client doesn't support it yet.
                        Your API key can be generated from the <strong>API Key</strong> tab.
                    </SetupDesc>

                    <Column gap='0'>
                        <SetupTabRow gap='0'>
                            <SetupTabBtn $active={setupTab === 'streamable'} onClick={() => setSetupTab('streamable')}>
                                Streamable HTTP
                                <RecommendedBadge>recommended</RecommendedBadge>
                            </SetupTabBtn>
                            <SetupTabBtn $active={setupTab === 'sse'} onClick={() => setSetupTab('sse')}>
                                SSE
                            </SetupTabBtn>
                        </SetupTabRow>

                        <SetupContent>
                            {setupTab === 'streamable' && (
                                <Column gap='12px'>
                                    <Paragraph>
                                        Uses the <InlineCode>streamable-http</InlineCode> transport over HTTPS. Supported by Claude Desktop, Cursor, and most modern MCP clients.
                                        The API key is passed in the <InlineCode>Authorization</InlineCode> header as a Bearer token.
                                    </Paragraph>
                                    <Column gap='4px'>
                                        <SectionLabel>MCP URL</SectionLabel>
                                        <CodeBlock><code>https://mcp.leaddelta.com/mcp</code></CodeBlock>
                                    </Column>
                                    <Column gap='4px'>
                                        <SectionLabel>Configuration</SectionLabel>
                                        <ApiKeyCodeBlock visible={keyVisible} onToggle={toggleKeyVisible} showToggle={!!ownApiKey}>
                                            <CodeBlock><code>{streamableConfig}</code></CodeBlock>
                                        </ApiKeyCodeBlock>
                                    </Column>
                                    <TipBox>
                                        Replace <InlineCode>YOUR_API_KEY</InlineCode> with your actual API key from the <strong>API Key</strong> tab.
                                    </TipBox>
                                </Column>
                            )}

                            {setupTab === 'sse' && (
                                <Column gap='12px'>
                                    <Paragraph>
                                        Uses the <InlineCode>sse</InlineCode> (Server-Sent Events) transport. Use this if your MCP client does not yet support <InlineCode>streamable-http</InlineCode>.
                                        The API key is passed in the <InlineCode>key</InlineCode> header.
                                    </Paragraph>
                                    <Column gap='4px'>
                                        <SectionLabel>MCP URL</SectionLabel>
                                        <CodeBlock><code>https://mcp.leaddelta.com/sse</code></CodeBlock>
                                    </Column>
                                    <Column gap='4px'>
                                        <SectionLabel>Configuration</SectionLabel>
                                        <ApiKeyCodeBlock visible={keyVisible} onToggle={toggleKeyVisible} showToggle={!!ownApiKey}>
                                            <CodeBlock><code>{sseConfig}</code></CodeBlock>
                                        </ApiKeyCodeBlock>
                                    </Column>
                                    <TipBox>
                                        Replace <InlineCode>YOUR_API_KEY</InlineCode> with your actual API key from the <strong>API Key</strong> tab.
                                    </TipBox>
                                </Column>
                            )}
                        </SetupContent>
                    </Column>
                </Column>

                {/* Tool reference */}
                <Column gap='0'>
                    <Text $heading5>Tool reference</Text>
                    <SetupDesc>Detailed input parameters for each tool.</SetupDesc>

                    <Column gap='0'>
                        <SetupTabRow gap='0'>
                            <SetupTabBtn $active={toolTab === 'filter'} onClick={() => setToolTab('filter')}>
                                get_connections_mcp
                            </SetupTabBtn>
                            <SetupTabBtn $active={toolTab === 'list'} onClick={() => setToolTab('list')}>
                                get_connections_list
                            </SetupTabBtn>
                            <SetupTabBtn $active={toolTab === 'leads'} onClick={() => setToolTab('leads')}>
                                search_leads
                            </SetupTabBtn>
                            <SetupTabBtn $active={toolTab === 'intros'} onClick={() => setToolTab('intros')}>
                                warm intros
                            </SetupTabBtn>
                        </SetupTabRow>

                        <SetupContent>
                            {toolTab === 'list' && (
                                <Column gap='16px'>
                                    <Paragraph>Retrieve a paginated list of all connections associated with your LeadDelta account.</Paragraph>
                                    <Column gap='8px'>
                                        <SectionLabel>Parameters</SectionLabel>
                                        <ParamTable>
                                            <thead><tr><th>Parameter</th><th>Type</th><th>Default</th><th>Description</th></tr></thead>
                                            <tbody>
                                                <tr>
                                                    <td><InlineCode>skip</InlineCode></td>
                                                    <td>integer</td>
                                                    <td><code>0</code></td>
                                                    <td>Number of records to skip for pagination. Minimum: 0</td>
                                                </tr>
                                                <tr>
                                                    <td><InlineCode>limit</InlineCode></td>
                                                    <td>integer</td>
                                                    <td><code>60</code></td>
                                                    <td>Maximum number of records to return. Range: 1–1000</td>
                                                </tr>
                                            </tbody>
                                        </ParamTable>
                                    </Column>
                                </Column>
                            )}

                            {toolTab === 'filter' && (
                                <Column gap='16px'>
                                    <Paragraph>
                                        Retrieve a filtered list of connections. Name, company, job title, location and headline use partial, case-insensitive matching;
                                        tag filters match the tag exactly. Every filter can be combined with the others.
                                    </Paragraph>
                                    <Column gap='8px'>
                                        <SectionLabel>Parameters</SectionLabel>
                                        <ParamTable>
                                            <thead><tr><th>Parameter</th><th>Type</th><th>Default</th><th>Description</th></tr></thead>
                                            <tbody>
                                                <tr><td><InlineCode>firstName</InlineCode></td><td>string</td><td>—</td><td>Filter by first name (partial match)</td></tr>
                                                <tr><td><InlineCode>lastName</InlineCode></td><td>string</td><td>—</td><td>Filter by last name (partial match)</td></tr>
                                                <tr><td><InlineCode>company</InlineCode></td><td>string</td><td>—</td><td>Filter by company name (partial match)</td></tr>
                                                <tr><td><InlineCode>jobTitle</InlineCode></td><td>string</td><td>—</td><td>Filter by job title (partial match)</td></tr>
                                                <tr><td><InlineCode>location</InlineCode></td><td>string</td><td>—</td><td>Filter by location (partial match)</td></tr>
                                                <tr><td><InlineCode>headline</InlineCode></td><td>string</td><td>—</td><td>Filter by LinkedIn headline (partial match)</td></tr>
                                                <tr><td><InlineCode>tags</InlineCode></td><td>list</td><td>—</td><td>Only people with these tags, by exact name</td></tr>
                                                <tr><td><InlineCode>tagIds</InlineCode></td><td>list</td><td>—</td><td>Only people with these tags, by tag id from <InlineCode>list_tags</InlineCode></td></tr>
                                                <tr><td><InlineCode>tagMatch</InlineCode></td><td>string</td><td><code>any</code></td><td><code>any</code>: at least one of the tags. <code>all</code>: every one of them</td></tr>
                                                <tr><td><InlineCode>excludeTags</InlineCode></td><td>list</td><td>—</td><td>Leave out people with any of these tags, by exact name</td></tr>
                                                <tr><td><InlineCode>excludeTagIds</InlineCode></td><td>list</td><td>—</td><td>Leave out people with any of these tags, by tag id</td></tr>
                                                <tr><td><InlineCode>skip</InlineCode></td><td>integer</td><td><code>0</code></td><td>Matches to skip, for the next page. Minimum: 0</td></tr>
                                                <tr><td><InlineCode>limit</InlineCode></td><td>integer</td><td><code>1000</code></td><td>Maximum records to return. Range: 1–1000</td></tr>
                                                <tr><td><InlineCode>format</InlineCode></td><td>string</td><td><code>full</code></td><td><code>compact</code>: one short row per person, for long lists. <code>full</code>: the whole record with notes</td></tr>
                                            </tbody>
                                        </ParamTable>
                                    </Column>
                                    <TipBox>
                                        Every answer includes the total number of matches, so your assistant can count a tag of thousands of people
                                        without listing them. Only tags you can see work as filters — a teammate's private tag does not.
                                    </TipBox>
                                    <Column gap='4px'>
                                        <SectionLabel>Example prompts</SectionLabel>
                                        <Column gap='6px'>
                                            <ExampleBox>"Find all my connections who work at Google in San Francisco"</ExampleBox>
                                            <ExampleBox>"How many people are tagged Trial, and which companies are they from?"</ExampleBox>
                                            <ExampleBox>"List everyone tagged Trial who is not tagged Customer"</ExampleBox>
                                        </Column>
                                    </Column>
                                </Column>
                            )}

                            {toolTab === 'leads' && (
                                <Column gap='16px'>
                                    <Paragraph>
                                        Search LeadDelta's lead database for people who are <strong>not</strong> in your network yet.
                                        Free, and it enrols nobody — results come back as candidates for you to look at.
                                    </Paragraph>
                                    <Column gap='8px'>
                                        <SectionLabel>Filters</SectionLabel>
                                        <ParamTable>
                                            <thead><tr><th>Filter</th><th>Type</th><th>Default</th><th>Description</th></tr></thead>
                                            <tbody>
                                                <tr><td><InlineCode>jobTitle</InlineCode></td><td>list</td><td>—</td><td>Job titles, e.g. Head of Sales, VP Sales</td></tr>
                                                <tr><td><InlineCode>school</InlineCode></td><td>list</td><td>—</td><td>Schools or universities attended</td></tr>
                                                <tr><td><InlineCode>city</InlineCode></td><td>list</td><td>—</td><td>The person's city</td></tr>
                                                <tr><td><InlineCode>state</InlineCode></td><td>list</td><td>—</td><td>The person's state or region</td></tr>
                                                <tr><td><InlineCode>countryCode</InlineCode></td><td>list</td><td>—</td><td>Two-letter country codes, e.g. DE, AT</td></tr>
                                                <tr><td><InlineCode>companyName</InlineCode></td><td>list</td><td>—</td><td>Employer names</td></tr>
                                                <tr><td><InlineCode>companyDomain</InlineCode></td><td>list</td><td>—</td><td>Employer website domains</td></tr>
                                                <tr><td><InlineCode>companyIndustry</InlineCode></td><td>list</td><td>—</td><td>Employer industries</td></tr>
                                                <tr><td><InlineCode>companySize</InlineCode></td><td>list</td><td>—</td><td>Employee-count bands, e.g. 11-50, 51-200</td></tr>
                                                <tr><td><InlineCode>companyCity</InlineCode></td><td>list</td><td>—</td><td>City the employer is based in</td></tr>
                                                <tr><td><InlineCode>companyState</InlineCode></td><td>list</td><td>—</td><td>State or region the employer is based in</td></tr>
                                                <tr><td><InlineCode>companyCountryCode</InlineCode></td><td>list</td><td>—</td><td>Employer country, two-letter code</td></tr>
                                                <tr><td><InlineCode>companyFounded</InlineCode></td><td>list</td><td>—</td><td>Exact founding years</td></tr>
                                                <tr><td><InlineCode>companyMinFounded</InlineCode></td><td>list</td><td>—</td><td>Earliest founding year to include</td></tr>
                                                <tr><td><InlineCode>companyMaxFounded</InlineCode></td><td>list</td><td>—</td><td>Latest founding year to include</td></tr>
                                                <tr><td><InlineCode>companyLinkedinUrl</InlineCode></td><td>list</td><td>—</td><td>Employer LinkedIn page URLs</td></tr>
                                                <tr><td><InlineCode>companyLinkedinId</InlineCode></td><td>list</td><td>—</td><td>Employer LinkedIn ids</td></tr>
                                                <tr><td><InlineCode>limit</InlineCode></td><td>integer</td><td><code>25</code></td><td>How many to return from this page. Range: 1–50</td></tr>
                                            </tbody>
                                        </ParamTable>
                                    </Column>
                                    <TipBox>
                                        Several values in one filter are alternatives — <InlineCode>jobTitle: CEO, Founder</InlineCode> finds either.
                                        Results come one page at a time, and your assistant can keep going through them.
                                    </TipBox>
                                    <Column gap='4px'>
                                        <SectionLabel>Example prompts</SectionLabel>
                                        <Column gap='6px'>
                                            <ExampleBox>"Find heads of RevOps at SaaS companies with 51-200 employees in Germany"</ExampleBox>
                                            <ExampleBox>"Show me founders of companies started after 2020 in fintech"</ExampleBox>
                                        </Column>
                                    </Column>
                                </Column>
                            )}

                            {toolTab === 'intros' && (
                                <Column gap='16px'>
                                    <Paragraph>
                                        You name the person you want to <strong>reach</strong>. LeadDelta works out who in your network
                                        can get you there, why that path works, and drafts the ask.
                                    </Paragraph>
                                    <Column gap='8px'>
                                        <SectionLabel>Parameters</SectionLabel>
                                        <ParamTable>
                                            <thead><tr><th>Parameter</th><th>Type</th><th>Default</th><th>Description</th></tr></thead>
                                            <tbody>
                                                <tr>
                                                    <td><InlineCode>connectionId</InlineCode></td>
                                                    <td>string</td>
                                                    <td>—</td>
                                                    <td>The person you want to be introduced to. A LeadDelta contact, or a LinkedIn profile link</td>
                                                </tr>
                                                <tr>
                                                    <td><InlineCode>importMissing</InlineCode></td>
                                                    <td>boolean</td>
                                                    <td><code>true</code></td>
                                                    <td>Whether new people may be added to your network so they can be considered</td>
                                                </tr>
                                            </tbody>
                                        </ParamTable>
                                    </Column>
                                    <WarningBox>
                                        A run costs <strong>10 credits</strong> and may add people to your network, tagged <strong>Imported</strong> —
                                        the person you named if they are not a contact yet, plus up to 25 of their mutual connections,
                                        since those are the people who could make the introduction.
                                    </WarningBox>
                                    <Column gap='4px'>
                                        <SectionLabel>Example prompts</SectionLabel>
                                        <Column gap='6px'>
                                            <ExampleBox>"Who can introduce me to the CTO of Stripe?"</ExampleBox>
                                            <ExampleBox>"Find me a warm intro to linkedin.com/in/some-profile"</ExampleBox>
                                        </Column>
                                    </Column>
                                </Column>
                            )}
                        </SetupContent>
                    </Column>
                </Column>

            </Column>
        </Container>
    )
}

// ─── Styled components ───────────────────────────────────────────────────────

const Container = styled.div`
    width: 100%;
    max-width: 860px;
    padding: 32px 16px 48px 16px;
`
const Paragraph = styled.p`
    font-family: NunitoSans;
    font-size: 14px;
    line-height: 22px;
    color: ${({ theme: { nonaryColor } }) => nonaryColor};
    margin: 0;
    display: block;
`
const SetupDesc = styled(Paragraph)`
    margin: 8px 0 16px 0;
`
const CodeBlock = styled.pre`
    background: ${styles.colors.black600};
    border: 1px solid ${styles.colors.black500};
    border-radius: 8px;
    padding: 16px;
    font-family: 'Courier New', Courier, monospace;
    font-size: 12px;
    line-height: 1.6;
    color: ${styles.colors.white};
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-all;
    margin: 0;

    code {
        background: none;
        padding: 0;
        border: none;
        font-size: inherit;
        color: inherit;
    }
`
const InlineCode = styled.code`
    background: ${({ theme: { tertiaryColor } }) => tertiaryColor};
    border: 1px solid ${({ theme: { quinaryColor } }) => quinaryColor};
    border-radius: 4px;
    padding: 1px 6px;
    font-family: 'Courier New', Courier, monospace;
    font-size: 12px;
    color: ${styles.colors.primary600};
    margin: 0 2px;
    white-space: nowrap;
`
const BetaBadge = styled.span`
    background: ${styles.colors.yellow600};
    color: ${styles.colors.white};
    border-radius: 4px;
    padding: 1px 7px;
    font-family: NunitoSans;
    font-size: 11px;
    font-weight: bold;
    letter-spacing: 0.05em;
    white-space: nowrap;
    margin-top: 2px;
    flex-shrink: 0;
`
const RecommendedBadge = styled.span`
    background: ${styles.colors.green200};
    color: ${styles.colors.green600};
    border-radius: 10px;
    padding: 1px 7px;
    font-family: NunitoSans;
    font-size: 10px;
    font-weight: bold;
    margin-left: 4px;
`
const TipBox = styled.div`
    background: ${styles.colors.primary100};
    border-left: 3px solid ${styles.colors.primary600};
    border-radius: 0 6px 6px 0;
    padding: 10px 14px;
    font-family: NunitoSans;
    font-size: 13px;
    line-height: 1.6;
    color: ${styles.colors.black400};
`
const WarningBox = styled(TipBox)`
    background: ${styles.colors.orange200};
    border-left-color: ${styles.colors.orange600};
`
const TYPE_COLOURS: Record<ToolType, { bg: string, fg: string }> = {
    read: { bg: styles.colors.green200, fg: styles.colors.green600 },
    write: { bg: styles.colors.primary100, fg: styles.colors.primary600 },
    care: { bg: styles.colors.red200, fg: styles.colors.red600 },
}
const TypeBadge = styled.span<{ $type: ToolType }>`
    display: inline-block;
    background: ${({ $type }) => TYPE_COLOURS[$type].bg};
    color: ${({ $type }) => TYPE_COLOURS[$type].fg};
    border-radius: 10px;
    padding: 1px 8px;
    font-family: NunitoSans;
    font-size: 10px;
    font-weight: bold;
    white-space: nowrap;
`
const LegendRow = styled(Row)`
    flex-wrap: wrap;
    row-gap: 6px;
`
const LegendItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-family: NunitoSans;
    font-size: 12px;
    color: ${styles.colors.black300};
`
const SectionLabel = styled.span`
    font-family: NunitoSans;
    font-size: 12px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${styles.colors.black300};
`
const ExampleBox = styled.div`
    background: ${({ theme: { secondaryColor } }) => secondaryColor};
    border: 1px solid ${({ theme: { quinaryColor } }) => quinaryColor};
    border-left: 3px solid ${styles.colors.black200};
    border-radius: 0 6px 6px 0;
    padding: 8px 14px;
    font-family: NunitoSans;
    font-size: 13px;
    font-style: italic;
    color: ${styles.colors.black400};
`
const SetupTabRow = styled(Row)`
    border-bottom: 1px solid ${({ theme: { quinaryColor } }) => quinaryColor};
    overflow-x: auto;
    &::-webkit-scrollbar { display: none; }
    -ms-overflow-style: none;
    scrollbar-width: none;
`
const SetupTabBtn = styled.div<{ $active: boolean }>`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    cursor: pointer;
    font-family: NunitoSans;
    font-size: 13px;
    font-weight: ${({ $active }) => $active ? 'bold' : 'normal'};
    color: ${({ $active }) => $active ? styles.colors.primary600 : styles.colors.black300};
    border-bottom: ${({ $active }) => $active ? `2px solid ${styles.colors.primary600}` : '2px solid transparent'};
    margin-bottom: -1px;
    white-space: nowrap;
    transition: color 0.15s;

    &:hover { color: ${styles.colors.primary600}; }
`
const SetupContent = styled.div`
    padding: 20px 0;
`
const ToolsTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-family: NunitoSans;
    font-size: 13px;
    background: ${styles.colors.white};
    border: 1px solid ${styles.colors.black200};
    border-radius: 8px;
    overflow: hidden;
    table-layout: fixed;

    th, td {
        /* Wide enough for the longest tool name, generate_connection_recommendations. */
        &:nth-child(1) { width: 320px; }
        &:nth-child(2) { width: auto; }
        &:nth-child(3) { width: 80px; text-align: right; }
    }

    /* The layout is fixed, so a name too long for its column would otherwise run
       across the description instead of wrapping. */
    td:nth-child(1) code {
        white-space: normal;
        word-break: break-word;
    }

    th {
        text-align: left;
        padding: 8px 12px;
        font-weight: bold;
        color: ${styles.colors.black300};
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        border-bottom: 1px solid ${styles.colors.black200};
        background: ${styles.colors.black100};
    }
    td {
        padding: 10px 12px;
        border-bottom: 1px solid ${styles.colors.black200};
        color: ${styles.colors.black400};
        vertical-align: middle;
    }
    tr:last-child td { border-bottom: none; }
`
const ParamTable = styled(ToolsTable)`
    th, td {
        &:nth-child(1) { width: 190px; }
        &:nth-child(2) { width: 70px; }
        &:nth-child(3) { width: 70px; text-align: left; }
        &:nth-child(4) { width: auto; }
    }
`
/** Two plain columns — no tool names, so the first column is not code. */
const CostTable = styled(ToolsTable)`
    th, td {
        &:nth-child(1) { width: 240px; }
        &:nth-child(2) { width: auto; text-align: left; }
        &:nth-child(3) { width: auto; }
    }
`
const HeaderRow = styled(Row)``
const DocLink = styled.a`
    color: ${styles.colors.primary600};
    font-weight: bold;
    text-decoration: none;
    &:hover { text-decoration: underline; }
`

export default McpServerDocs
